// src/services/termsService.js

import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

const CURRENT_TERMS_DOC_ID = 'current';

// Cache for terms data (persists until app restart)
let termsCache = null;

/**
 * Gets the current terms and conditions
 * @returns {Promise<Object|null>} Terms data or null if not found
 */
export async function getCurrentTerms() {
  // Return cached version if available
  if (termsCache) {
    return termsCache;
  }

  try {
    const termsRef = doc(db, 'termsAndConditions', CURRENT_TERMS_DOC_ID);
    const termsSnap = await getDoc(termsRef);
    
    if (termsSnap.exists()) {
      const termsData = { id: termsSnap.id, ...termsSnap.data() };
      
      // Cache the results
      termsCache = termsData;
      return termsData;
    } else {
      console.error('No terms and conditions found in database');
      throw new Error('Terms and conditions have not been configured. Please contact an administrator.');
    }
  } catch (error) {
    console.error('Error fetching current terms:', error);
    throw new Error(`Failed to fetch terms and conditions: ${error.message}`);
  }
}

/**
 * Creates or updates the current terms and conditions
 * @param {string} content - HTML content of the terms
 * @param {string} adminEmail - Email of the admin making the update
 * @returns {Promise<Object>} Updated terms data
 */
export async function updateCurrentTerms(content, adminEmail) {
  if (!content || content.trim().length === 0) {
    throw new Error('Terms content cannot be empty');
  }

  if (!adminEmail) {
    throw new Error('Admin email is required');
  }

  try {
    const termsRef = doc(db, 'termsAndConditions', CURRENT_TERMS_DOC_ID);
    
    // Get existing terms to preserve version history
    let existingTerms = null;
    let newVersion = '1.0.0';
    let previousVersions = [];
    
    try {
      const existingSnap = await getDoc(termsRef);
      if (existingSnap.exists()) {
        existingTerms = existingSnap.data();
        
        // Increment version number
        const currentVersion = existingTerms.version || '1.0.0';
        const versionParts = currentVersion.split('.').map(Number);
        versionParts[2] += 1; // Increment patch version
        newVersion = versionParts.join('.');
        
        // Add current version to history before updating
        previousVersions = existingTerms.previousVersions || [];
        previousVersions.push({
          version: existingTerms.version,
          content: existingTerms.content,
          lastUpdated: existingTerms.lastUpdated,
          updatedBy: existingTerms.updatedBy,
          archivedAt: new Date().toISOString()
        });
      }
    } catch (fetchError) {
      console.log('No existing terms found, creating initial version');
    }

    const updatedTerms = {
      content: content.trim(),
      version: newVersion,
      lastUpdated: serverTimestamp(),
      updatedBy: adminEmail,
      isActive: true,
      previousVersions: previousVersions
    };

    // Update Firestore
    await setDoc(termsRef, updatedTerms);

    // Clear cache and return updated data with timestamp converted
    termsCache = null;
    const finalData = {
      id: CURRENT_TERMS_DOC_ID,
      ...updatedTerms,
      lastUpdated: new Date().toISOString() // Convert for immediate return
    };
    
    // Cache the new data
    termsCache = finalData;
    
    return finalData;
  } catch (error) {
    console.error('Error updating terms:', error);
    throw new Error(`Failed to update terms and conditions: ${error.message}`);
  }
}

/**
 * Gets the complete version history of terms and conditions
 * @returns {Promise<Array>} Array of previous versions
 */
export async function getTermsHistory() {
  try {
    const termsRef = doc(db, 'termsAndConditions', CURRENT_TERMS_DOC_ID);
    const termsSnap = await getDoc(termsRef);
    
    if (termsSnap.exists()) {
      const termsData = termsSnap.data();
      return termsData.previousVersions || [];
    } else {
      return [];
    }
  } catch (error) {
    console.error('Error fetching terms history:', error);
    throw new Error(`Failed to fetch terms history: ${error.message}`);
  }
}

/**
 * Clears the terms cache (for testing or forced refresh)
 */
export function clearTermsCache() {
  termsCache = null;
}

/**
 * Validates terms content for basic requirements
 * @param {string} content - HTML content to validate
 * @returns {Object} Validation result with isValid and errors
 */
export function validateTermsContent(content) {
  const errors = [];
  
  if (!content || typeof content !== 'string') {
    errors.push('Content is required');
  } else if (content.trim().length === 0) {
    errors.push('Content cannot be empty');
  } else if (content.length < 100) {
    errors.push('Content seems too short for terms and conditions');
  } else if (content.length > 500000) {
    errors.push('Content is too long (maximum 500KB)');
  }
  
  return {
    isValid: errors.length === 0,
    errors: errors
  };
}

/**
 * Gets statistics about the terms content
 * @param {string} content - HTML content to analyze
 * @returns {Object} Statistics including word count, character count, etc.
 */
export function getTermsStats(content) {
  if (!content || typeof content !== 'string') {
    return {
      characterCount: 0,
      wordCount: 0,
      lineCount: 0
    };
  }
  
  // Strip HTML tags for word count
  const textOnly = content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  const wordCount = textOnly ? textOnly.split(' ').length : 0;
  
  return {
    characterCount: content.length,
    wordCount: wordCount,
    lineCount: content.split('\n').length
  };
}

/**
 * Default terms and conditions template
 * @returns {string} Template terms and conditions content
 */
export function getTermsTemplate() {
  const currentDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  
  return `
<div style="max-width: 800px; margin: 0 auto; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.6; color: #333;">
  
  <div style="text-align: center; padding: 40px 20px; background: linear-gradient(135deg, #001f3f 0%, #003366 100%); color: white; border-radius: 10px; margin-bottom: 40px;">
    <h1 style="margin: 0 0 10px 0; font-size: 2.5em; font-weight: 600;">Terms and Conditions</h1>
    <p style="margin: 0; opacity: 0.9; font-size: 1.1em;">Effective Date: ${currentDate}</p>
  </div>

  <div style="background: #f8f9fa; border-left: 4px solid #001f3f; padding: 20px; border-radius: 5px; margin-bottom: 30px;">
    <p style="margin: 0; font-size: 1.05em;"><strong>Welcome to BackPass!</strong> We're building the future of European hostel travel. These terms explain how our service works and what to expect. We aim for transparency and fairness in everything we do.</p>
  </div>

  <section style="margin-bottom: 35px;">
    <h2 style="color: #001f3f; border-bottom: 2px solid #e9ecef; padding-bottom: 10px; margin-bottom: 20px;">1. Agreement to Terms</h2>
    <div style="background: white; padding: 20px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
      <p>By creating an account or using BackPass services, you agree to these Terms and Conditions. You must be at least 18 years old and have the legal capacity to enter into contracts.</p>
      <div style="background: #e7f3ff; padding: 15px; border-radius: 5px; margin-top: 15px;">
        <p style="margin: 0;"><strong>📋 Key Requirements:</strong></p>
        <ul style="margin: 10px 0 0 0; padding-left: 20px;">
          <li>Minimum age: 18 years</li>
          <li>Valid email and phone number</li>
          <li>Accurate account information</li>
        </ul>
      </div>
    </div>
  </section>

  <section style="margin-bottom: 35px;">
    <h2 style="color: #001f3f; border-bottom: 2px solid #e9ecef; padding-bottom: 10px; margin-bottom: 20px;">2. Our Service</h2>
    <div style="background: white; padding: 20px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
      <h3 style="color: #001f3f; margin-top: 0;">What We Provide</h3>
      <ul style="list-style: none; padding: 0;">
        <li style="padding: 8px 0; border-bottom: 1px solid #f0f0f0;">✓ Journey planning and routing through European hostels</li>
        <li style="padding: 8px 0; border-bottom: 1px solid #f0f0f0;">✓ Accommodation booking coordination</li>
        <li style="padding: 8px 0; border-bottom: 1px solid #f0f0f0;">✓ Secure payment processing via Stripe</li>
        <li style="padding: 8px 0; border-bottom: 1px solid #f0f0f0;">✓ Real-time pricing and availability</li>
        <li style="padding: 8px 0;">✓ 24/7 access to your journey details</li>
      </ul>
      
      <h3 style="color: #001f3f; margin-top: 25px;">Service Limitations</h3>
      <p>BackPass acts as a technology platform connecting travelers with accommodations. We are not a licensed travel agent or tour operator. Users are responsible for:</p>
      <ul>
        <li>Ensuring valid travel documents</li>
        <li>Travel insurance</li>
        <li>Personal safety and belongings</li>
        <li>Compliance with local laws</li>
      </ul>
    </div>
  </section>

  <section style="margin-bottom: 35px;">
    <h2 style="color: #001f3f; border-bottom: 2px solid #e9ecef; padding-bottom: 10px; margin-bottom: 20px;">3. Bookings and Payments</h2>
    <div style="background: white; padding: 20px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
      <h3 style="color: #001f3f; margin-top: 0;">Payment Processing</h3>
      <ul>
        <li>All payments processed securely through <strong>Stripe</strong></li>
        <li>Prices displayed in your selected currency</li>
        <li>Payment required at time of booking</li>
        <li>We do not store credit card information</li>
      </ul>
      
      <h3 style="color: #001f3f;">Pricing</h3>
      <p>Journey prices may vary based on:</p>
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-top: 10px;">
        <div style="background: #f8f9fa; padding: 10px; border-radius: 5px;">📅 Travel dates</div>
        <div style="background: #f8f9fa; padding: 10px; border-radius: 5px;">🏨 Accommodation availability</div>
        <div style="background: #f8f9fa; padding: 10px; border-radius: 5px;">⏰ Booking lead time</div>
        <div style="background: #f8f9fa; padding: 10px; border-radius: 5px;">👥 Group size</div>
      </div>
      
      <div style="background: #fff3cd; padding: 15px; border-radius: 5px; margin-top: 20px; border-left: 4px solid #ffc107;">
        <p style="margin: 0;"><strong>⚠️ Important:</strong> Some accommodations may require additional payment upon arrival. These local charges are separate from your BackPass journey purchase.</p>
      </div>
    </div>
  </section>

  <section style="margin-bottom: 35px;">
    <h2 style="color: #001f3f; border-bottom: 2px solid #e9ecef; padding-bottom: 10px; margin-bottom: 20px;">4. Cancellations and Refunds</h2>
    <div style="background: white; padding: 20px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
      <h3 style="color: #001f3f; margin-top: 0;">Refund Policy</h3>
      <p>Refunds are available only in the following circumstances:</p>
      <table style="width: 100%; border-collapse: collapse; margin-top: 15px;">
        <tr style="background: #f8f9fa;">
          <td style="padding: 12px; border: 1px solid #dee2e6;"><strong>Situation</strong></td>
          <td style="padding: 12px; border: 1px solid #dee2e6;"><strong>Refund Available</strong></td>
        </tr>
        <tr>
          <td style="padding: 12px; border: 1px solid #dee2e6;">Service completely unavailable</td>
          <td style="padding: 12px; border: 1px solid #dee2e6; color: green;">✓ Yes</td>
        </tr>
        <tr>
          <td style="padding: 12px; border: 1px solid #dee2e6;">Duplicate charge</td>
          <td style="padding: 12px; border: 1px solid #dee2e6; color: green;">✓ Yes</td>
        </tr>
        <tr>
          <td style="padding: 12px; border: 1px solid #dee2e6;">Unauthorized charge</td>
          <td style="padding: 12px; border: 1px solid #dee2e6; color: green;">✓ Yes</td>
        </tr>
        <tr>
          <td style="padding: 12px; border: 1px solid #dee2e6;">Change of travel plans</td>
          <td style="padding: 12px; border: 1px solid #dee2e6; color: red;">✗ No</td>
        </tr>
        <tr>
          <td style="padding: 12px; border: 1px solid #dee2e6;">Accommodation quality concerns</td>
          <td style="padding: 12px; border: 1px solid #dee2e6; color: red;">✗ No</td>
        </tr>
      </table>
      <p style="margin-top: 20px;"><strong>To request a refund:</strong> Email support@backpass.co with your journey code and reason for request within 48 hours of the issue.</p>
    </div>
  </section>

  <section style="margin-bottom: 35px;">
    <h2 style="color: #001f3f; border-bottom: 2px solid #e9ecef; padding-bottom: 10px; margin-bottom: 20px;">5. User Responsibilities</h2>
    <div style="background: white; padding: 20px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
      <h3 style="color: #001f3f; margin-top: 0;">Your Account</h3>
      <ul>
        <li>Keep your login credentials secure</li>
        <li>Notify us immediately of unauthorized access</li>
        <li>Maintain accurate account information</li>
        <li>One account per person</li>
      </ul>
      
      <h3 style="color: #001f3f;">Travel Requirements</h3>
      <div style="background: #e7f3ff; padding: 15px; border-radius: 5px;">
        <p style="margin: 0 0 10px 0;"><strong>You are responsible for:</strong></p>
        <ul style="margin: 0; padding-left: 20px;">
          <li>Valid passport and visas</li>
          <li>Travel insurance</li>
          <li>Health requirements and vaccinations</li>
          <li>Understanding local laws and customs</li>
          <li>Arriving during hostel check-in hours</li>
        </ul>
      </div>
      
      <h3 style="color: #001f3f; margin-top: 25px;">Prohibited Activities</h3>
      <p>You may not:</p>
      <ul>
        <li>Create multiple or false accounts</li>
        <li>Use the service for illegal purposes</li>
        <li>Resell or transfer bookings</li>
        <li>Interfere with service operations</li>
        <li>Harass other users or staff</li>
      </ul>
    </div>
  </section>

  <section style="margin-bottom: 35px;">
    <h2 style="color: #001f3f; border-bottom: 2px solid #e9ecef; padding-bottom: 10px; margin-bottom: 20px;">6. Limitation of Liability</h2>
    <div style="background: white; padding: 20px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
      <p>To the maximum extent permitted by law, BackPass's total liability for any claims shall not exceed the amount paid for your journey.</p>
      
      <div style="background: #f8f9fa; padding: 15px; border-radius: 5px; margin-top: 15px;">
        <p style="margin: 0 0 10px 0;"><strong>We are not responsible for:</strong></p>
        <ul style="margin: 0; padding-left: 20px;">
          <li>Third-party accommodation services</li>
          <li>Travel delays or cancellations</li>
          <li>Lost or stolen belongings</li>
          <li>Personal injury at accommodations</li>
          <li>Force majeure events</li>
        </ul>
      </div>
    </div>
  </section>

  <section style="margin-bottom: 35px;">
    <h2 style="color: #001f3f; border-bottom: 2px solid #e9ecef; padding-bottom: 10px; margin-bottom: 20px;">7. Privacy and Data</h2>
    <div style="background: white; padding: 20px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
      <p>Your privacy is important to us. Our Privacy Policy (available at <a href="https://backpass.co/privacy" style="color: #001f3f;">backpass.co/privacy</a>) explains how we collect, use, and protect your information.</p>
      
      <h3 style="color: #001f3f;">Key Points:</h3>
      <ul>
        <li>We use industry-standard encryption</li>
        <li>We don't sell your personal data</li>
        <li>You can request data deletion</li>
        <li>We comply with GDPR and CCPA</li>
      </ul>
    </div>
  </section>

  <section style="margin-bottom: 35px;">
    <h2 style="color: #001f3f; border-bottom: 2px solid #e9ecef; padding-bottom: 10px; margin-bottom: 20px;">8. Dispute Resolution</h2>
    <div style="background: white; padding: 20px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
      <h3 style="color: #001f3f; margin-top: 0;">Governing Law</h3>
      <p>These Terms are governed by the laws of Delaware, United States.</p>
      
      <h3 style="color: #001f3f;">Resolution Process</h3>
      <ol>
        <li><strong>Direct Contact:</strong> First, contact us at support@backpass.co</li>
        <li><strong>Mediation:</strong> If unresolved, we'll attempt good-faith mediation</li>
        <li><strong>Arbitration:</strong> Final disputes resolved through binding arbitration</li>
      </ol>
    </div>
  </section>

  <section style="margin-bottom: 35px;">
    <h2 style="color: #001f3f; border-bottom: 2px solid #e9ecef; padding-bottom: 10px; margin-bottom: 20px;">9. Changes to Terms</h2>
    <div style="background: white; padding: 20px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
      <p>We may update these Terms as our service evolves. When we make significant changes:</p>
      <ul>
        <li>We'll notify you via email</li>
        <li>We'll post a notice in the app</li>
        <li>The updated date will change</li>
      </ul>
      <p>Continued use after changes means you accept the new terms.</p>
    </div>
  </section>

  <section style="margin-bottom: 35px;">
    <h2 style="color: #001f3f; border-bottom: 2px solid #e9ecef; padding-bottom: 10px; margin-bottom: 20px;">10. Contact Us</h2>
    <div style="background: white; padding: 20px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
      <p style="margin-bottom: 20px;">We're here to help! Reach out with any questions or concerns:</p>
      
      <div style="display: grid; gap: 15px;">
        <div style="background: #f8f9fa; padding: 15px; border-radius: 5px; border-left: 4px solid #001f3f;">
          <strong>📧 Email:</strong> support@backpass.co<br>
          <small>Response time: 1-2 business days</small>
        </div>
        
        <div style="background: #f8f9fa; padding: 15px; border-radius: 5px; border-left: 4px solid #001f3f;">
          <strong>📱 Phone:</strong> +1-214-315-6968<br>
          <small>Business hours: Mon-Fri, 9am-5pm CST</small>
        </div>
        
        <div style="background: #f8f9fa; padding: 15px; border-radius: 5px; border-left: 4px solid #001f3f;">
          <strong>🏢 Legal Entity:</strong><br>
          BackPass LLC<br>
          A Delaware Limited Liability Company
        </div>
      </div>
    </div>
  </section>

  <div style="background: #001f3f; color: white; padding: 30px; border-radius: 10px; text-align: center; margin-top: 50px;">
    <h3 style="margin: 0 0 15px 0;">Ready to Explore Europe?</h3>
    <p style="margin: 0; opacity: 0.9;">Thank you for choosing BackPass. We're committed to making your European adventure unforgettable while protecting your rights and privacy.</p>
  </div>

  <div style="text-align: center; padding: 30px 0; color: #666; font-size: 0.9em;">
    <p>© ${new Date().getFullYear()} BackPass LLC. All rights reserved.</p>
    <p>Terms and Conditions • Version 1.0 • Last Updated: ${currentDate}</p>
  </div>

</div>`;
}